import React, { useContext, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import {
  Route,
  BrowserRouter,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom'
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query'

import ReaderPage from './pages/reader/ReaderPage'
import ExplorePage from './pages/explore/ExplorePage'
import { useLatestSession } from './hooks/useSession'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import './index.css'
import OrgPage from './pages/orgs/:orgId/OrgPage'
import OrgSignUpPage from './pages/orgs/:orgId/signUp'
import ReaderTutorial from './pages/tutorials/reader/ReaderTutorial'
import InstallAppTutorial from './pages/tutorials/installApp/InstallAppTutorial'
import AuthProvider, {
  AuthContext,
} from './providers/AuthProvider'
import Spinner from './components/Spinner'
import RequestAndroidAccountDeletionPage from './pages/account/request-android-account-deletion'
import PrivacyPolicyPage from './pages/account/privacy-policy'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
)

const App = () => {
  const { latestSession } = useLatestSession()
  const { isLoading } = useContext(AuthContext)
  const { pathname } = useLocation()
  const queryClient = useQueryClient()

  // Remove cached sessions when navigating away from the 'reader'
  useEffect(() => {
    if (pathname?.indexOf('/reader') === 0) return
    queryClient.invalidateQueries(['session'])
  }, [pathname, queryClient])

  if (isLoading) {
    return (
      <div className="min-h-4 mx-auto mb-4 flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <Routes>
      <Route path="account">
        <Route
          path="privacy-policy"
          element={<PrivacyPolicyPage />}
        />
        <Route
          path="request-android-account-deletion"
          element={<RequestAndroidAccountDeletionPage />}
        />
      </Route>

      <Route
        path="/reader/:documentId"
        element={<ReaderPage />}
      />

      <Route path="/" element={<ExplorePage />} />

      {latestSession?.documentId && (
        <Route
          path="*"
          element={
            <Navigate
              to={`/reader/${latestSession.documentId}`}
            />
          }
        />
      )}

      <Route path="/orgs/:orgId">
        <Route index element={<OrgPage />} />
        <Route
          path="sign-up/:code"
          element={<OrgSignUpPage />}
        />
      </Route>

      <Route path="/tutorials">
        <Route path="reader" element={<ReaderTutorial />} />
        <Route
          path="install-app"
          element={<InstallAppTutorial />}
        />
      </Route>
      <Route
        path="*"
        element={<Navigate to={'/'} />}
      />
    </Routes>
  )
}

root.render(
  <React.StrictMode>
    <AuthProvider>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </BrowserRouter>
    </AuthProvider>
  </React.StrictMode>,
)

serviceWorkerRegistration.unregister()
